import AvatarLightbox from "@/components/AvatarLightbox"
import ErrorDialog from "@/components/ErrorDialog"
import FollowButton from "@/components/FollowButton"
import MediaFrame from "@/components/MediaFrame"
import { useAuth } from "@/hooks/useAuth"
import { useAuthModal } from "@/hooks/useAuthModal"
import { connectWallet, shortenAddress } from "@/lib/chain"
import { CITY_KEY, INTEREST_TOPICS, parseInterests } from "@/lib/culture"
import { isActivePost, isSeedPost, postOwnerId, postPath, toPublicPost } from "@/lib/filters"
import { lumi } from "@/lib/lumi"
import UsernameMark from "@/components/UsernameMark"
import { emptyProfileForm, instagramUrl, isOfficialAccount, isOwnProfile, isReservedOfficialUsername, isSeedProfile, isSeedUserId, OFFICIAL_USERNAME, publicProfilesFor, sanitizeHandle, sanitizeUrl, tiktokUrl, toPublicProfile, usernameTaken, xUrl } from "@/lib/profile"
import { privacyPublicProfiles } from "@/lib/privacy"
import { blockAccount, listBlockedIds, unblockAccount } from "@/lib/social"
import { uploadFile } from "@/lib/upload"
import type { Follow, Like, Post, Profile } from "@/types"
import { useEffect, useRef, useState } from "react"
import { Link, useParams, useSearchParams } from "react-router-dom"

export default function ProfilePage() {
  const { userId: routeUserId } = useParams()
  const [searchParams] = useSearchParams()
  const { user, isAuthenticated } = useAuth()
  const { openAuth } = useAuthModal()
  const avatarRef = useRef<HTMLInputElement>(null)
  const tabsRef = useRef<HTMLDivElement>(null)
  const savingRef = useRef(false)
  const loadedIdRef = useRef("")
  const [profile, setProfile] = useState<Profile | null>(null)
  const [posts, setPosts] = useState<Post[]>([])
  const [form, setForm] = useState(emptyProfileForm())
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState("")
  const [saved, setSaved] = useState(false)
  const [loading, setLoading] = useState(true)
  const [followers, setFollowers] = useState<Follow[]>([])
  const [following, setFollowing] = useState<Follow[]>([])
  const [savedPosts, setSavedPosts] = useState<Post[]>([])
  const [likedPosts, setLikedPosts] = useState<Post[]>([])
  const [gridTab, setGridTab] = useState<"posts" | "liked" | "saved" | "prefs">(
    searchParams.get("tab") === "prefs" ? "prefs" : "posts",
  )
  const [suggested, setSuggested] = useState<Profile[]>([])
  const [copied, setCopied] = useState(false)
  const [photoOpen, setPhotoOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [blockedIds, setBlockedIds] = useState<string[]>([])
  const [blocking, setBlocking] = useState(false)
  const selectedInterests = parseInterests(form.interests)

  const viewingId = routeUserId || user?.userId || ""
  const isOwn = isOwnProfile(viewingId, user?.userId) && isAuthenticated

  const applyProfile = (nextProfile: Profile | null, own: boolean) => {
    setProfile(nextProfile)
    if (!nextProfile) {
      setForm((prev) => ({
        ...emptyProfileForm(),
        avatarUrl: own ? prev.avatarUrl || "" : "",
      }))
      return
    }
    setForm((prev) => ({
      username: nextProfile.username || "",
      displayName: nextProfile.displayName || "",
      bio: nextProfile.bio || "",
      walletAddress: own ? nextProfile.walletAddress || "" : "",
      avatarUrl: nextProfile.avatarUrl || (own ? prev.avatarUrl || "" : ""),
      websiteUrl: nextProfile.websiteUrl || "",
      instagramHandle: nextProfile.instagramHandle || "",
      xHandle: nextProfile.xHandle || "",
      tiktokHandle: nextProfile.tiktokHandle || "",
      youtubeUrl: nextProfile.youtubeUrl || "",
      city: nextProfile.city || "",
      interests: nextProfile.interests || "",
      privacy: nextProfile.privacy === "followers" ? "followers" : "public",
    }))
  }

  const load = async (userId: string, own: boolean, options?: { silent?: boolean }) => {
    const silent = Boolean(options?.silent || savingRef.current || loadedIdRef.current === userId)
    if (!silent) setLoading(true)
    try {
      const viewerId = user?.userId || ""
      const [profileRes, postRes, followerRes, followingRes, likeRes, bookmarkRes, blocked] = await Promise.all([
        own
          ? lumi.entities.profiles.list({ filter: { userId }, limit: 1 })
          : privacyPublicProfiles({ userIds: [userId] }).then((list) => ({ list })),
        lumi.entities.posts.list({
          filter: { $or: [{ ownerId: userId }, { authorId: userId }] },
          sort: { createdAt: -1 },
          limit: 80,
        }),
        lumi.entities.follows.list({ filter: { followingId: userId }, limit: 200 }),
        lumi.entities.follows.list({ filter: { followerId: userId }, limit: 200 }),
        own ? lumi.entities.likes.list({ filter: { userId }, limit: 200 }) : Promise.resolve({ list: [] }),
        own ? lumi.entities.bookmarks.list({ filter: { userId }, limit: 200 }) : Promise.resolve({ list: [] }),
        listBlockedIds(viewerId),
      ])
      setBlockedIds(blocked)
      const matched = ((profileRes?.list || []) as Profile[]).find((item) => item.userId === userId) || null
      const scoped = matched?.userId === userId && !isSeedProfile(matched) ? matched : null
      const nextProfile = own ? scoped : toPublicProfile(scoped)
      applyProfile(isSeedUserId(userId) && !own ? null : nextProfile, own)
      setPosts(
        ((postRes?.list || []) as Post[])
          .filter((post) => isActivePost(post) && !isSeedPost(post) && postOwnerId(post) === userId && !isSeedUserId(userId))
          .map((post) => toPublicPost(post, viewerId)),
      )
      setFollowers(((followerRes?.list || []) as Follow[]).filter((item) => item.followingId === userId))
      setFollowing(((followingRes?.list || []) as Follow[]).filter((item) => item.followerId === userId))
      const myLikes = ((likeRes?.list || []) as Like[]).filter((item) => item.userId === userId)
      const myBookmarks = ((bookmarkRes?.list || []) as { userId?: string; postId?: string }[]).filter((item) => item.userId === userId)
      if (own) {
        const extraIds = Array.from(new Set([...myLikes.map((item) => item.postId), ...myBookmarks.map((item) => item.postId)].filter(Boolean)))
        const extraRes = extraIds.length
          ? await lumi.entities.posts.list({ filter: { _id: { $in: extraIds } }, limit: 80 })
          : { list: [] }
        const extraPosts = ((extraRes?.list || []) as Post[])
          .filter((post) => isActivePost(post) && !blocked.includes(post.authorId) && !blocked.includes(post.ownerId || ""))
          .map((post) => toPublicPost(post, viewerId))
        const extraMap = new Map(extraPosts.map((post) => [post._id, post]))
        setLikedPosts(myLikes.map((item) => extraMap.get(item.postId)).filter(Boolean) as Post[])
        setSavedPosts(myBookmarks.map((item) => extraMap.get(item.postId)).filter(Boolean) as Post[])
      } else {
        setLikedPosts([])
        setSavedPosts([])
      }
      if (own) {
        const people = await privacyPublicProfiles({ suggested: true })
        const followingIds = new Set(((followingRes?.list || []) as Follow[]).filter((item) => item.followerId === userId).map((item) => item.followingId))
        setSuggested(
          publicProfilesFor(people)
            .filter((item) => item.userId && item.userId !== userId && !followingIds.has(item.userId) && !blocked.includes(item.userId) && !isSeedProfile(item))
            .slice(0, 12),
        )
      } else {
        setSuggested([])
      }
      loadedIdRef.current = userId
    } catch (err) {
      console.error("Profile load failed:", err)
      setError("Could not load profile.")
      applyProfile(null, own)
      setPosts([])
      setFollowers([])
      setFollowing([])
    } finally {
      setLoading(false)
    }
  }

  const openTab = (id: "posts" | "liked" | "saved" | "prefs") => {
    if (!isOwn && id !== "posts") return
    setGridTab(id)
    setEditing(false)
    window.requestAnimationFrame(() => {
      tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    })
  }

  useEffect(() => {
    if (!isOwn && gridTab !== "posts") setGridTab("posts")
    if (!isOwn) setEditing(false)
  }, [isOwn, gridTab])

  useEffect(() => {
    if (!isOwn) return
    const tab = searchParams.get("tab")
    if (tab === "prefs" || tab === "liked" || tab === "saved" || tab === "posts") {
      setGridTab(tab)
    }
  }, [isOwn, searchParams])

  useEffect(() => {
    if (!routeUserId && (!isAuthenticated || !user?.userId)) {
      openAuth()
      return
    }
    if (!viewingId || savingRef.current) return
    load(viewingId, Boolean(isAuthenticated && viewingId === user?.userId), {
      silent: loadedIdRef.current === viewingId,
    })
  }, [isAuthenticated, user?.userId, routeUserId, viewingId, openAuth])

  const saveProfile = async () => {
    if (!user?.userId || !isOwn) return
    const requested = sanitizeHandle(form.username)
    const displayName = form.displayName.trim()
    if (!displayName || !requested) {
      setError("Add a username and handle before saving.")
      return
    }
    if (!/^[A-Za-z0-9._]{2,24}$/.test(requested)) {
      setError("Usernames can only use 2–24 letters, numbers, periods, or underscores.")
      return
    }
    if (isReservedOfficialUsername(requested) && !isOfficialAccount({ userId: user.userId })) {
      setError("@KLIPO is reserved for the official KLIPO account.")
      return
    }
    const username = isOfficialAccount({ userId: user.userId }) ? OFFICIAL_USERNAME : requested
    savingRef.current = true
    setSaving(true)
    setError("")
    try {
      const taken = await privacyPublicProfiles({ username })
      if (usernameTaken(taken, username, user.userId)) {
        setError("That @username is already taken.")
        return
      }
      const now = new Date().toISOString()
      const payload = {
        userId: user.userId,
        username,
        displayName,
        bio: form.bio.trim(),
        avatarUrl: form.avatarUrl || "",
        walletAddress: form.walletAddress.trim(),
        websiteUrl: sanitizeUrl(form.websiteUrl),
        instagramHandle: sanitizeHandle(form.instagramHandle),
        xHandle: sanitizeHandle(form.xHandle),
        tiktokHandle: sanitizeHandle(form.tiktokHandle),
        youtubeUrl: sanitizeUrl(form.youtubeUrl),
        city: form.city.trim(),
        interests: selectedInterests.join(","),
        privacy: form.privacy || "public",
        onboarded: true,
        creator: user.userId,
        creatorEmail: user.email || profile?.creatorEmail || "",
        createdAt: profile?.createdAt || now,
        updatedAt: now,
      }
      let savedProfile: Profile | null = null
      if (profile?._id && profile.userId === user.userId) {
        await lumi.entities.profiles.update(profile._id, payload)
        savedProfile = { ...profile, ...payload, _id: profile._id }
      } else {
        const created = await lumi.entities.profiles.create(payload) as Profile
        savedProfile = created?._id ? created : { ...payload, _id: created?._id || profile?._id || "" }
      }
      applyProfile(savedProfile, true)
      loadedIdRef.current = user.userId
      const chosenCity = form.city.trim()
      if (chosenCity) localStorage.setItem(CITY_KEY, chosenCity)
      else localStorage.removeItem(CITY_KEY)
      setSaved(true)
      setEditing(false)
    } catch (err) {
      console.error("Profile save failed:", err)
      const message = err instanceof Error && err.message ? err.message : "Could not save profile."
      setError(message)
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }

  const linkWallet = async () => {
    if (!isOwn) return
    try {
      const address = await connectWallet()
      setForm((prev) => ({ ...prev, walletAddress: address }))
    } catch (err) {
      setError((err as Error)?.message || "Wallet connect failed.")
    }
  }

  const handleAvatar = async (file?: File | null) => {
    if (!isOwn || !file) return
    if (!file.type.startsWith("image/")) {
      setError("Use a photo for your avatar.")
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("Keep avatars under 10MB.")
      return
    }
    setUploading(true)
    try {
      const url = await uploadFile(file)
      setForm((prev) => ({ ...prev, avatarUrl: url }))
    } catch (err) {
      console.error("Avatar upload failed:", err)
      setError((err as Error)?.message || "Could not upload avatar.")
    } finally {
      setUploading(false)
    }
  }

  const isBlocked = blockedIds.includes(viewingId)

  const copyProfile = async () => {
    const url = `${window.location.origin}/u/${viewingId}`
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1400)
    } catch (err) {
      console.error("Copy profile failed:", err)
    }
  }

  const toggleBlock = async () => {
    if (!user?.userId || isOwn || !viewingId) {
      if (!user?.userId) openAuth()
      return
    }
    setBlocking(true)
    try {
      if (isBlocked) await unblockAccount(user.userId, viewingId)
      else await blockAccount(user.userId, viewingId)
      await load(viewingId, false, { silent: true })
    } catch (err) {
      console.error("Block update failed:", err)
      setError(isBlocked ? "Could not unblock this account." : "Could not block this account.")
    } finally {
      setBlocking(false)
    }
  }

  if (!routeUserId && !isAuthenticated) {
    return <p className="px-4 py-10 text-center text-sm text-[#6E6E6E]">Sign in to open your profile.</p>
  }

  const toggleInterest = (id: string) => {
    setForm((prev) => {
      const current = parseInterests(prev.interests)
      const next = current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
      return { ...prev, interests: next.join(",") }
    })
  }

  const gridPosts = gridTab === "liked" ? likedPosts : gridTab === "saved" ? savedPosts : posts

  const publicLinks = [
    form.websiteUrl ? { label: "Website", href: sanitizeUrl(form.websiteUrl) } : null,
    form.instagramHandle ? { label: `Instagram @${sanitizeHandle(form.instagramHandle)}`, href: instagramUrl(form.instagramHandle) } : null,
    form.xHandle ? { label: `X @${sanitizeHandle(form.xHandle)}`, href: xUrl(form.xHandle) } : null,
    form.tiktokHandle ? { label: `TikTok @${sanitizeHandle(form.tiktokHandle)}`, href: tiktokUrl(form.tiktokHandle) } : null,
    form.youtubeUrl ? { label: "YouTube", href: sanitizeUrl(form.youtubeUrl) } : null,
  ].filter(Boolean) as { label: string; href: string }[]

  const chipClass = "rounded-lg border border-[#1F1F1F] bg-[#141414] px-3 py-1.5 text-center text-xs font-semibold text-white"

  return (
    <div className="pb-4">
      <div className="px-4 pt-5 lg:px-8">
        <div className="flex items-center gap-6">
          <button
            type="button"
            onClick={() => {
              if (form.avatarUrl) setPhotoOpen(true)
              else if (isOwn) avatarRef.current?.click()
            }}
            className="h-[86px] w-[86px] shrink-0 overflow-hidden rounded-full border border-[#1F1F1F] bg-[#141414]"
            aria-label={form.avatarUrl ? "View profile photo" : isOwn ? "Change avatar" : "Avatar"}
          >
            {form.avatarUrl ? (
              <img src={form.avatarUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="flex h-full w-full items-center justify-center text-sm text-[#6E6E6E]">
                {isOwn ? (uploading ? "..." : "+") : ""}
              </span>
            )}
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate font-[Syne] text-xl font-bold">{form.displayName || "Username"}</h1>
            <p className="truncate text-sm text-[#A8A8A8]">
              <UsernameMark username={form.username} userId={viewingId} fallback="handle" />
            </p>
            <div className="mt-3 flex items-center justify-between gap-2 text-center text-xs text-[#A8A8A8]">
              <span><strong className="block text-base text-white">{posts.length}</strong> posts</span>
              <span><strong className="block text-base text-white">{followers.length}</strong> followers</span>
              <span><strong className="block text-base text-white">{following.length}</strong> following</span>
            </div>
          </div>
        </div>

        {isOwn || !isBlocked ? (
          <div className="mt-4 min-w-0">
            {form.city ? <p className="text-xs text-[#8A8A8A]">{form.city}</p> : null}
            {form.bio ? <p className="mt-1 whitespace-pre-wrap text-sm text-white">{form.bio}</p> : null}
            {publicLinks.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                {publicLinks.map((link) => (
                  <a key={link.href} href={link.href} target="_blank" rel="noreferrer" className="text-xs text-[#00C805]">
                    {link.label}
                  </a>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className={`mt-4 grid gap-2 ${isOwn ? "grid-cols-3" : "grid-cols-2 sm:grid-cols-4"}`}>
          {!isOwn ? (
            <>
              <div className="[&>button]:w-full">
                <FollowButton
                  targetUserId={viewingId}
                  following={followers.find((item) => item.followerId === user?.userId) || null}
                  onChanged={() => load(viewingId, false, { silent: true })}
                />
              </div>
              {isBlocked ? (
                <span className={`${chipClass} text-[#6E6E6E]`}>Message</span>
              ) : (
                <Link to={`/messages/${viewingId}`} className={chipClass}>
                  Message
                </Link>
              )}
              <button type="button" onClick={copyProfile} className={chipClass}>
                {copied ? "Copied" : "Share"}
              </button>
              <button type="button" disabled={blocking} onClick={toggleBlock} className={chipClass}>
                {blocking ? "..." : isBlocked ? "Unblock" : "Block"}
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={() => setEditing((prev) => !prev)} className={chipClass}>
                {editing ? "Close editor" : "Edit profile"}
              </button>
              <button type="button" onClick={copyProfile} className={chipClass}>
                {copied ? "Copied" : "Share"}
              </button>
              <Link to="/settings" className={chipClass}>
                Settings
              </Link>
            </>
          )}
        </div>
      </div>

      {isOwn ? (
        <input
          ref={avatarRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(event) => handleAvatar(event.target.files?.[0])}
        />
      ) : null}

      {isOwn && editing ? (
        <div className="mt-5 space-y-3 px-4 lg:px-8">
          <p className="text-[11px] text-[#6E6E6E]">{uploading ? "Uploading avatar..." : "Tap the circle to add a photo."}</p>
          <input value={form.displayName} onChange={(event) => setForm((prev) => ({ ...prev, displayName: event.target.value }))} placeholder="Username" className="w-full rounded-xl border border-[#1F1F1F] bg-[#0B0B0B] px-4 py-3 text-sm outline-none" />
          <input
            value={isOfficialAccount({ userId: user?.userId }) ? OFFICIAL_USERNAME : form.username}
            onChange={(event) => setForm((prev) => ({ ...prev, username: event.target.value }))}
            placeholder="Handle"
            disabled={isOfficialAccount({ userId: user?.userId })}
            className="w-full rounded-xl border border-[#1F1F1F] bg-[#0B0B0B] px-4 py-3 text-sm outline-none disabled:text-[#A8A8A8]"
          />
          <p className="text-[11px] text-[#6E6E6E]">@usernames are unique. @KLIPO is reserved for the official account.</p>
          <textarea value={form.bio} onChange={(event) => setForm((prev) => ({ ...prev, bio: event.target.value }))} placeholder="Bio" rows={3} className="w-full rounded-xl border border-[#1F1F1F] bg-[#0B0B0B] px-4 py-3 text-sm outline-none" />
          <input value={form.websiteUrl} onChange={(event) => setForm((prev) => ({ ...prev, websiteUrl: event.target.value }))} placeholder="Website" className="w-full rounded-xl border border-[#1F1F1F] bg-[#0B0B0B] px-4 py-3 text-sm outline-none" />
          <input value={form.instagramHandle} onChange={(event) => setForm((prev) => ({ ...prev, instagramHandle: event.target.value }))} placeholder="Instagram handle" className="w-full rounded-xl border border-[#1F1F1F] bg-[#0B0B0B] px-4 py-3 text-sm outline-none" />
          <input value={form.xHandle} onChange={(event) => setForm((prev) => ({ ...prev, xHandle: event.target.value }))} placeholder="X handle" className="w-full rounded-xl border border-[#1F1F1F] bg-[#0B0B0B] px-4 py-3 text-sm outline-none" />
          <input value={form.tiktokHandle} onChange={(event) => setForm((prev) => ({ ...prev, tiktokHandle: event.target.value }))} placeholder="TikTok handle" className="w-full rounded-xl border border-[#1F1F1F] bg-[#0B0B0B] px-4 py-3 text-sm outline-none" />
          <input value={form.youtubeUrl} onChange={(event) => setForm((prev) => ({ ...prev, youtubeUrl: event.target.value }))} placeholder="YouTube URL" className="w-full rounded-xl border border-[#1F1F1F] bg-[#0B0B0B] px-4 py-3 text-sm outline-none" />
          <div className="flex gap-2">
            <input value={form.walletAddress} onChange={(event) => setForm((prev) => ({ ...prev, walletAddress: event.target.value }))} placeholder="ETH wallet" className="w-full rounded-xl border border-[#1F1F1F] bg-[#0B0B0B] px-4 py-3 text-sm outline-none" />
            <button type="button" onClick={linkWallet} className="shrink-0 rounded-xl border border-[#1F1F1F] px-3 text-xs text-[#00C805]">
              Connect
            </button>
          </div>
          {form.walletAddress ? <p className="text-[11px] text-[#6E6E6E]">{shortenAddress(form.walletAddress)} · Robinhood Chain</p> : null}
          <p className="text-[11px] text-[#6E6E6E]">Your wallet and email stay on this account. Public visitors only see bio, links, and posts.</p>
          <button type="button" disabled={saving} onClick={saveProfile} className="w-full rounded-xl bg-[#00C805] py-3 text-sm font-semibold text-black disabled:opacity-60">
            {saving ? "Saving..." : "Save profile"}
          </button>
        </div>
      ) : null}

      <div ref={tabsRef} className="mt-6 flex items-center border-t border-[#1F1F1F]">
        {([
          { id: "posts" as const, label: "Posts" },
          ...(isOwn
            ? [
                { id: "liked" as const, label: "Liked" },
                { id: "saved" as const, label: "Saved" },
                { id: "prefs" as const, label: "Preferences" },
              ]
            : []),
        ]).map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => openTab(id)}
            className={`relative min-h-11 flex-1 px-2 text-[11px] font-semibold uppercase tracking-wider ${
              gridTab === id ? "text-white" : "text-[#6E6E6E]"
            }`}
          >
            {label}
            {gridTab === id ? <span className="absolute inset-x-6 -top-px h-px bg-white" /> : null}
          </button>
        ))}
      </div>

      {!isOwn && isBlocked ? (
        <p className="mt-10 px-4 text-center text-sm text-[#6E6E6E]">You blocked this account. Unblock to see their posts again.</p>
      ) : isOwn && gridTab === "prefs" ? (
        <div className="mt-5 space-y-5 px-4 lg:px-8">
          <section>
            <h2 className="text-sm font-semibold">What do you want to see?</h2>
            <p className="mt-1 text-xs text-[#6E6E6E]">Pick topics for For You. Saved to your account.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {INTEREST_TOPICS.map((topic) => {
                const on = selectedInterests.includes(topic.id)
                return (
                  <button
                    key={topic.id}
                    type="button"
                    onClick={() => toggleInterest(topic.id)}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                      on ? "bg-[#00FF66] text-black" : "border border-[#1F1F1F] text-[#A8A8A8]"
                    }`}
                  >
                    {topic.label}
                  </button>
                )
              })}
            </div>
          </section>
          <section>
            <h2 className="text-sm font-semibold">Your location</h2>
            <p className="mt-1 text-xs text-[#6E6E6E]">Optional. Type your location, or leave it blank.</p>
            <input
              value={form.city}
              onChange={(event) => setForm((prev) => ({ ...prev, city: event.target.value }))}
              placeholder="Location"
              className="mt-3 w-full rounded-xl border border-[#1F1F1F] bg-[#0B0B0B] px-4 py-3 text-sm outline-none"
            />
          </section>
          <section>
            <h2 className="text-sm font-semibold">Recommended follows</h2>
            <p className="mt-1 text-xs text-[#6E6E6E]">People to fill your Following feed. Follow or skip anytime.</p>
            {suggested.length === 0 ? (
              <p className="mt-3 text-sm text-[#6E6E6E]">No extra recommendations right now.</p>
            ) : (
              <div className="mt-3 space-y-3">
                {suggested.map((person) => (
                  <div key={person.userId} className="flex items-center justify-between gap-3">
                    <Link to={`/u/${person.userId}`} className="flex min-w-0 items-center gap-3">
                      <span className="h-10 w-10 overflow-hidden rounded-full border border-[#1F1F1F] bg-[#141414]">
                        {person.avatarUrl ? <img src={person.avatarUrl} alt="" className="h-full w-full object-cover" /> : null}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold">{person.displayName || "Creator"}</span>
                        <UsernameMark username={person.username} userId={person.userId} className="text-xs text-[#8A8A8A]" />
                        {person.bio ? <span className="mt-0.5 block truncate text-[11px] text-[#6E6E6E]">{person.bio}</span> : null}
                      </span>
                    </Link>
                    <FollowButton
                      targetUserId={person.userId}
                      following={following.find((item) => item.followingId === person.userId) || null}
                      onChanged={() => user?.userId ? load(user.userId, true, { silent: true }) : undefined}
                    />
                  </div>
                ))}
              </div>
            )}
          </section>
          <button type="button" disabled={saving} onClick={saveProfile} className="w-full rounded-xl bg-[#00C805] py-3 text-sm font-semibold text-black disabled:opacity-60">
            {saving ? "Saving..." : "Save preferences"}
          </button>
        </div>
      ) : loading && gridPosts.length === 0 && !saving && !saved ? (
        <div className="px-3 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-1 sm:grid-cols-4 lg:grid-cols-6">
            {Array.from({ length: 9 }).map((_, index) => (
              <div key={index} className="aspect-square animate-pulse bg-[#141414]" />
            ))}
          </div>
        </div>
      ) : gridPosts.length === 0 ? (
        <p className="mt-10 px-4 text-center text-sm text-[#6E6E6E]">
          {gridTab === "liked" ? "No liked clips yet." : gridTab === "saved" ? "No saved clips yet." : "No live photos or videos on this profile."}
        </p>
      ) : (
        <div className="px-3 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-1 sm:grid-cols-4 lg:grid-cols-6">
            {gridPosts.map((post) => (
              <Link key={post._id} to={postPath(post)} state={{ post }} className="relative aspect-square overflow-hidden bg-black transition-opacity active:opacity-80">
                <MediaFrame post={post} preview autoPlay={false} fill />
              </Link>
            ))}
          </div>
        </div>
      )}

      {saved ? (
        <div className="fixed inset-0 z-[120] flex items-end justify-center bg-black/70 p-4 sm:items-center">
          <div className="w-full max-w-sm rounded-2xl border border-[#1F1F1F] bg-[#141414] p-5 text-center shadow-2xl">
            <p className="font-[Syne] text-2xl font-bold text-[#00C805]">Profile saved</p>
            <p className="mt-2 text-sm text-[#A8A8A8]">Your KLIPO profile is up to date.</p>
            <button
              type="button"
              onClick={() => setSaved(false)}
              className="mt-5 w-full rounded-xl bg-[#00C805] py-2.5 text-sm font-semibold text-black"
            >
              Close
            </button>
          </div>
        </div>
      ) : null}
      <AvatarLightbox
        src={form.avatarUrl}
        open={photoOpen}
        onClose={() => setPhotoOpen(false)}
        actionLabel={isOwn ? "Change photo" : undefined}
        onAction={isOwn ? () => {
          setPhotoOpen(false)
          avatarRef.current?.click()
        } : undefined}
      />
      {error ? <ErrorDialog message={error} onClose={() => setError("")} /> : null}
    </div>
  )
}
